import React from 'react';
import classnames from 'classnames';
import {
  Provider,
  Dialog,
  DialogContainer,
  Content,
  defaultTheme,
  Flex,
  View,
  Heading,
  Text,
  Button,
  ProgressCircle,
} from "@adobe/react-spectrum";
import Grid from 'material-ui/Grid';
import Tooltip from 'material-ui/Tooltip';
import {MultiSelect} from '../ui/react-aria/MultiSelect';
import '../ui/react-aria/MultiSelect.css';
import {EnhancedDateRangePicker} from '../ui/EnhancedDateRangePicker';
import {withStyles} from "material-ui/styles/index";
import styles, {chartColors} from "./styles";
import {BarChart3, Info, ScatterChart as ScatterChartIcon} from 'lucide-react';
import MetricsBarGraphModal from './MetricsBarGraphModal';
import MetricsScatterGraphModal from './MetricsScatterGraphModal';
import {dateFormatter} from "../../common/utils";

const metricsStageTranslationKeys = {
  Starting: "metrics_stage_starting",
  "Reading tests": "metrics_stage_reading_tests",
  "Loading test data": "metrics_stage_loading_test_data",
  Finishing: "metrics_stage_finishing",
  Completed: "metrics_stage_completed",
  Failed: "metrics_stage_failed",
};

class Metrics extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      dateRange: null,
      customerIds: [],
      jobIds: [],
      testerTypes: [],
      cableTypes: [],
      technicians: [],
      testStandards: [],
      cableBrands: [],
      chartModalOpen: false,
      chartModalTitle: '',
      chartModalData: [],
      scatterModalOpen: false,
      scatterModalTitle: '',
      scatterModalData: [],
      scatterModalUnit: ' dB',
    };

    this.debounceTimer = null;
  }

  componentDidMount() {
    // Fetch initial filter options with empty state
    this.fetchFilters();
  }

  componentDidUpdate(prevProps) {
    const {filters} = this.props;
    if (filters && filters !== prevProps.filters) {
      this.cleanupStaleSelections(filters);
    }
  }

  /**
   * Remove selected IDs that are no longer present in the updated filter options.
   */
  cleanupStaleSelections = (filters) => {
    const stateUpdates = {};

    if (filters.customers) {
      const validCustomerIds = new Set(filters.customers.map(c => c.id));
      const cleaned = this.state.customerIds.filter(id => validCustomerIds.has(id));
      if (cleaned.length !== this.state.customerIds.length) {
        stateUpdates.customerIds = cleaned;
      }
    }

    if (filters.jobs) {
      const validJobIds = new Set(filters.jobs.map(j => j.id));
      const cleaned = this.state.jobIds.filter(id => validJobIds.has(id));
      if (cleaned.length !== this.state.jobIds.length) {
        stateUpdates.jobIds = cleaned;
      }
    }

    const stringFilters = ['testerTypes', 'cableTypes', 'technicians', 'testStandards', 'cableBrands'];
    stringFilters.forEach(key => {
      if (filters[key]) {
        const validValues = new Set(filters[key]);
        const cleaned = this.state[key].filter(v => validValues.has(v));
        if (cleaned.length !== this.state[key].length) {
          stateUpdates[key] = cleaned;
        }
      }
    });

    if (Object.keys(stateUpdates).length > 0) {
      this.setState(stateUpdates);
    }
  };

  buildFilterPayload = (overrides = {}) => {
    const state = {...this.state, ...overrides};
    const payload = {};

    if (state.dateRange && state.dateRange.start) {
      payload.dateFrom = state.dateRange.start.toString();
    }
    if (state.dateRange && state.dateRange.end) {
      payload.dateTo = state.dateRange.end.toString();
    }
    if (state.customerIds.length > 0) payload.customerIds = state.customerIds;
    if (state.jobIds.length > 0) payload.jobIds = state.jobIds;
    if (state.testerTypes.length > 0) payload.testerTypes = state.testerTypes;
    if (state.cableTypes.length > 0) payload.cableTypes = state.cableTypes;
    if (state.technicians.length > 0) payload.technicians = state.technicians;
    if (state.testStandards.length > 0) payload.testStandards = state.testStandards;
    if (state.cableBrands.length > 0) payload.cableBrands = state.cableBrands;

    return payload;
  };

  fetchFilters = (overrides = {}) => {
    if (this.debounceTimer) clearTimeout(this.debounceTimer);
    this.debounceTimer = setTimeout(() => {
      const payload = this.buildFilterPayload(overrides);
      this.props.onFiltersChanged(payload);
    }, 500);
  };

  openChartModal = (title, cableData) => {
    if (!cableData || !cableData.chartData || cableData.chartData.length === 0) return;
    const {translate} = this.props;
    const datasets = [
      {label: translate('metrics_chart_passed'), color: chartColors.passed, data: cableData.chartData.map(p => ({date: p.date, value: p.passedCount}))},
      {label: translate('failed'), color: chartColors.failed, data: cableData.chartData.map(p => ({date: p.date, value: p.failedCount}))},
      {label: translate('metrics_chart_total'), color: chartColors.total, isTotal: true, data: cableData.chartData.map(p => ({date: p.date, value: p.totalCount}))},
    ];
    this.setState({chartModalOpen: true, chartModalTitle: title, chartModalData: datasets});
  };

  closeChartModal = () => {
    this.setState({chartModalOpen: false});
  };

  openScatterModal = (metricName, chartData, unit = ' dB') => {
    if (!chartData || chartData.length === 0) return;
    const {translate} = this.props;
    const showAverageSeries = !['PoE Class', 'Link Speed'].includes(metricName);
    const datasets = [
      {label: translate('metrics_chart_actual'), color: chartColors.actual, data: chartData.flatMap(p => (p.values || []).map(v => ({date: p.date, value: v})))},
      {label: translate('metrics_min'), color: chartColors.min, data: chartData.map(p => ({date: p.date, value: p.min}))},
      {label: translate('metrics_max'), color: chartColors.max, data: chartData.map(p => ({date: p.date, value: p.max}))},
      ...(showAverageSeries ? [{label: translate('metrics_chart_average'), color: chartColors.average, data: chartData.map(p => ({date: p.date, value: p.average}))}] : []),
    ];
    this.setState({scatterModalOpen: true, scatterModalTitle: metricName, scatterModalData: datasets, scatterModalUnit: unit});
  };

  closeScatterModal = () => {
    this.setState({scatterModalOpen: false});
  };

  openFiberScatterModal = (title, chartData, unit) => {
    if (!chartData || chartData.length === 0) return;
    const datasets = [
      {label: title, color: chartColors.average, data: chartData.map(p => ({date: p.date, value: p.value}))},
    ];
    this.setState({scatterModalOpen: true, scatterModalTitle: title, scatterModalData: datasets, scatterModalUnit: unit || ''});
  };

  handleFilterChange = (key, value) => {
    this.setState({[key]: value}, () => {
      this.fetchFilters();
    });
  };

  handleDateRangeChange = (dateRange) => {
    this.setState({dateRange}, () => {
      this.fetchFilters();
    });
  };

  handleApplyFilters = () => {
    const payload = this.buildFilterPayload();
    this.props.onApplyFilters(payload);
  };

  handleClearFilters = () => {
    const { onResetFilters } = this.props;
    this.setState({
      dateRange: null,
      customerIds: [],
      jobIds: [],
      testerTypes: [],
      cableTypes: [],
      technicians: [],
      testStandards: [],
      cableBrands: [],
    }, () => {
      this.fetchFilters();
      onResetFilters();
    });
  };

  hasAnyFilterSelection = () => {
    const {dateRange, customerIds, jobIds, testerTypes, cableTypes, technicians, testStandards, cableBrands} = this.state;
    return (dateRange && dateRange.start) ||
      customerIds.length > 0 ||
      jobIds.length > 0 ||
      testerTypes.length > 0 ||
      cableTypes.length > 0 ||
      technicians.length > 0 ||
      testStandards.length > 0 ||
      cableBrands.length > 0;
  };

  isDateRangeSelected = () => {
    const {dateRange} = this.state;
    return dateRange && dateRange.start;
  };

  /**
   * Convert a plain string array to {id, name} items for MultiSelect.
   */
  toItems = (arr) => {
    if (!arr) return [];
    return arr.map(value => ({id: value, name: value}));
  };

  toTranslatedItems = (arr) => {
    if (!arr) return [];
    const {translate} = this.props;
    return arr.map(value => ({id: value, name: translate(value)}));
  };

  renderFilters() {
    const {filters, translate, classes, metricsLoading, filtersLoading, metricsError} = this.props;
    const {dateRange, customerIds, jobIds, testerTypes, cableTypes, technicians, testStandards, cableBrands} = this.state;

    const customers = filters ? (filters.customers || []) : [];
    const jobs = filters ? (filters.jobs || []) : [];
    const controlsDisabled = metricsLoading || filtersLoading;
    const filterDisabled = controlsDisabled || !this.isDateRangeSelected();

    return (
      <div className={classes.metricsFilters}>
        <Grid container spacing={16} alignItems="flex-end">
          <Grid item xs={3}>
            <EnhancedDateRangePicker
              label={translate('metrics_date_range')}
              value={dateRange}
              onChange={this.handleDateRangeChange}
              isDisabled={controlsDisabled}
              width="100%"
              translate={translate}
              locale={this.props.activeLanguage}
            />
          </Grid>
          <Grid item xs={3}>
            <MultiSelect
              label={translate('profile_customer')}
              items={customers}
              selectedIds={customerIds}
              onSelectionChange={(val) => this.handleFilterChange('customerIds', val)}
              isDisabled={filterDisabled}
            />
          </Grid>
          <Grid item xs={3}>
            <MultiSelect
              label={translate('general_job')}
              items={jobs}
              selectedIds={jobIds}
              onSelectionChange={(val) => this.handleFilterChange('jobIds', val)}
              isDisabled={filterDisabled}
            />
          </Grid>
          <Grid item xs={3}>
            <MultiSelect
              label={translate('metrics_tester_type')}
              items={this.toTranslatedItems(filters ? filters.testerTypes : [])}
              selectedIds={testerTypes}
              onSelectionChange={(val) => this.handleFilterChange('testerTypes', val)}
              isDisabled={filterDisabled}
            />
          </Grid>
          <Grid item xs={3}>
            <MultiSelect
              label={translate('result_cabletype')}
              items={this.toItems(filters ? filters.cableTypes : [])}
              selectedIds={cableTypes}
              onSelectionChange={(val) => this.handleFilterChange('cableTypes', val)}
              isDisabled={filterDisabled}
            />
          </Grid>
          <Grid item xs={3}>
            <MultiSelect
              label={translate('metrics_technician')}
              items={this.toItems(filters ? filters.technicians : [])}
              selectedIds={technicians}
              onSelectionChange={(val) => this.handleFilterChange('technicians', val)}
              isDisabled={filterDisabled}
            />
          </Grid>
          <Grid item xs={3}>
            <MultiSelect
              label={translate('test_standard')}
              items={this.toItems(filters ? filters.testStandards : [])}
              selectedIds={testStandards}
              onSelectionChange={(val) => this.handleFilterChange('testStandards', val)}
              isDisabled={filterDisabled}
            />
          </Grid>
          <Grid item xs={3}>
            <MultiSelect
              label={translate('cable_brand')}
              items={this.toItems(filters ? filters.cableBrands : [])}
              selectedIds={cableBrands}
              onSelectionChange={(val) => this.handleFilterChange('cableBrands', val)}
              isDisabled={filterDisabled}
            />
          </Grid>
        </Grid>
        {filtersLoading && (
          <Flex direction="row" alignItems="center" gap="size-100" marginTop="size-150">
            <ProgressCircle size="S" isIndeterminate aria-label={translate('metrics_loading_filters')} />
          </Flex>
        )}
        {!this.isDateRangeSelected() && (
          <p className={classes.dateRangeWarning}>{translate('metrics_select_date_range_first')}</p>
        )}
        {filters && filters.testCount > 0 && (
          <Flex direction="row" alignItems="center" gap="size-100" marginTop="size-200">
            <Text>{translate('metrics_test_count')}:</Text>
            <Text>{filters.testCount}</Text>
            <Tooltip
              title={translate('metrics_test_count_tooltip')}
              disableTriggerFocus={true}
              classes={{tooltip: classes.testCountTooltip}}
            >
              <span className={classes.testCountInfoIcon}>
                <Info size={16} aria-hidden="true" />
              </span>
            </Tooltip>
          </Flex>
        )}
        <Flex direction="row" gap="size-200" marginTop="size-200">
          <Button
            variant="accent"
            onPress={this.handleApplyFilters}
            isDisabled={!this.hasAnyFilterSelection() || metricsLoading || filtersLoading}
            UNSAFE_className={classes.applyButton}
          >
            {translate('metrics_apply')}
          </Button>
          <Button variant="secondary" onPress={this.handleClearFilters} isDisabled={metricsLoading || filtersLoading} UNSAFE_className={classes.clearButton}>
            {translate('metrics_clear')}
          </Button>
        </Flex>
        {metricsError && (
          <Text UNSAFE_className={classes.metricsError}>{metricsError}</Text>
        )}
      </div>
    );
  }

  formatDuration = (seconds) => {
    const {translate} = this.props;
    if (!seconds || seconds < 60) {
      return `${Math.max(1, Math.round(seconds || 0))} ${translate('metrics_seconds')}`;
    }

    const minutes = Math.floor(seconds / 60);
    const remainder = Math.round(seconds % 60);
    return remainder > 0
      ? `${minutes} ${translate('metrics_minutes')} ${remainder} ${translate('metrics_seconds')}`
      : `${minutes} ${translate('metrics_minutes')}`;
  };

  renderProgressDialog() {
    const {metricsLoading, metricsRunProgress, classes, translate} = this.props;
    if (!metricsLoading) return null;

    const progress = metricsRunProgress || {};
    const percentage = Math.max(0, Math.min(100, progress.percentage || 0));
    const stageTranslationKey = metricsStageTranslationKeys[progress.stage];
    const stage = stageTranslationKey
      ? translate(stageTranslationKey)
      : progress.stage || translate('metrics_preparing');
    const processedCount = progress.processedCount || 0;
    const totalCount = progress.totalCount || 0;
    const hasEstimate = progress.estimatedSecondsRemaining !== null &&
      progress.estimatedSecondsRemaining !== undefined &&
      progress.estimatedSecondsRemaining > 0;

    return (
      <DialogContainer isDismissable={false} onDismiss={() => undefined}>
        <Dialog>
          <Heading>{translate('metrics_generating')}</Heading>
          <Content>
            <Flex direction="column" gap="size-200" UNSAFE_className={classes.progressDialogContent}>
              <Text UNSAFE_className={classes.progressStage}>{stage}</Text>
              <div className={classes.progressTrack}>
                <div
                  className={classes.progressFill}
                  style={{width: `${percentage}%`}}
                />
              </div>
              <div className={classes.progressMeta}>
                <span>{percentage}%</span>
                <span>
                  {totalCount > 0
                    ? `${processedCount} / ${totalCount} ${translate('home_tests')}`
                    : translate('metrics_preparing_result_set')}
                </span>
              </div>
              {hasEstimate && (
                <Text UNSAFE_className={classes.progressHint}>
                  {translate('metrics_estimated_time_remaining')}: {this.formatDuration(progress.estimatedSecondsRemaining)}
                </Text>
              )}
            </Flex>
          </Content>
        </Dialog>
      </DialogContainer>
    );
  }

  renderHeader() {
    const {metrics, classes, activeLanguage, translate} = this.props;
    if (!metrics) return null;

    const cards = [
      {
        label: translate('metrics_first_test'),
        value: metrics.firstTestAt ? dateFormatter(metrics.firstTestAt, activeLanguage) : translate('metrics_not_available'),
        unit: "",
        labelClassName: classes.summaryCardTitle,
      },
      {
        label: translate('metrics_last_test'),
        value: metrics.lastTestAt ? dateFormatter(metrics.lastTestAt, activeLanguage) : translate('metrics_not_available'),
        unit: "",
        labelClassName: classes.summaryCardTitle,
      },
      {label: translate('general_customers'), value: metrics.customerCount, unit: '', labelClassName: classes.summaryCardTitle},
      {label: translate('general_jobs'), value: metrics.jobCount, unit: '', labelClassName: classes.summaryCardTitle},
    ];

    return (
      <View marginTop="size-400">
        <Heading level={3} marginBottom="size-200">{translate('metrics_overview')}</Heading>
        <div className={classes.grid4}>
          {cards.map((card) => this.renderMetricCard(card))}
        </div>
      </View>
    );
  }

  renderCableTypeSummary() {
    const {metrics, classes, translate} = this.props;
    if (!metrics) return null;

    const renderCableGroup = (title, cableData) => {
      if (!cableData) return null;
      const rows = [
        {label: translate('home_tests'), value: cableData.testCount, unit: ''},
        {label: translate('metrics_chart_passed'), value: cableData.passedTestCount, unit: ''},
        {label: translate('failed'), value: cableData.failedTestCount, unit: ''},
        {label: translate('success_test_rate'), value: this.formatDecimal(cableData.passedTestPercent, 1), unit: "%"},
        {label: translate('metrics_length'), value: cableData.length ?? translate('metrics_not_available'), unit: metrics.unit},
      ];
      return (
        <div key={title} className={classes.cableTypeCard}>
          <Heading level={4} marginBottom="size-200" UNSAFE_className={classes.summaryCardTitle}>{title}</Heading>
          {rows.map(row => (
            <div key={row.label} className={classes.cableTypeRow}>
              <span className={classes.cableTypeRowLabel}>{row.label}</span>
              <span className={classes.cableTypeRowValue}>{row.value}</span>
              {row.unit && <span className={classes.cableTypeRowUnit}>{row.unit}</span>}
            </div>
          ))}
          {cableData.chartData && cableData.chartData.length > 0 && (
            <div className={classes.chartIconWrapper}>
              <BarChart3
                size={20}
                className={classes.chartIcon}
                onClick={() => this.openChartModal(title, cableData)}
              />
            </div>
          )}
        </div>
      );
    };

    return (
      <View marginTop="size-400">
        <Heading level={3} marginBottom="size-200">{translate('metrics_cable_type_summary')}</Heading>
        <div className={classes.grid4}>
          {renderCableGroup(translate('copper'), metrics.copper)}
          {renderCableGroup(translate('metrics_coax'), metrics.coax)}
          {renderCableGroup(translate('fiber'), metrics.fiber)}
          {renderCableGroup("VDV II/PoE PRO", metrics.mobile)}
        </div>
      </View>
    );
  }

  renderTechnicians() {
    const {metrics, classes, translate} = this.props;
    if (!metrics || !metrics.technicians || metrics.technicians.length === 0) return null;

    return (
      <View marginTop="size-400">
        <Heading level={3} marginBottom="size-200">{translate('metrics_technicians')}</Heading>
        <Flex direction="row" wrap gap="size-100">
          {metrics.technicians.map(tech => (
            <div key={tech} className={classes.technicianPill}>
              {tech}
            </div>
          ))}
        </Flex>
      </View>
    );
  }

  renderMarginCard(s) {
    const {classes, translate} = this.props;
    const rows = [
      {label: translate('metrics_min'), value: this.formatDecimal(s.lowestMargin), unit: 'dB'},
      {label: translate('metrics_max'), value: this.formatDecimal(s.highestMargin), unit: 'dB'},
      {label: translate('metrics_average_short'), value: this.formatDecimal(s.averageMargin), unit: 'dB'},
    ];
    return (
      <div key={s.metric} className={classes.marginCard}>
        <div className={classnames(classes.marginCardMetric, classes.summaryCardTitle)}>{s.metric}</div>
        {rows.map(row => (
          <div key={row.label} className={classes.marginCardRow}>
            <span className={classes.marginCardRowLabel}>{row.label}</span>
            <span className={classes.marginCardRowValue}>{row.value}</span>
            <span className={classes.marginCardRowUnit}>{row.unit}</span>
          </div>
        ))}
        {s.chartData && s.chartData.length > 0 && (
          <div className={classes.chartIconWrapper}>
            <ScatterChartIcon
              size={20}
              className={classes.chartIcon}
              onClick={() => this.openScatterModal(s.metric, s.chartData)}
            />
          </div>
        )}
      </div>
    );
  }

  getMobileUnit(metric) {
    const {metrics} = this.props;
    if (metric === 'Length') return metrics ? metrics.unit : '';
    if (metric === 'Link Speed') return 'Gb/s';
    if (metric === 'PoE Watts') return 'W';
    return '';
  }

  renderMobileMarginCard(s) {
    const {classes, translate} = this.props;
    const unit = this.getMobileUnit(s.metric);
    const scatterUnit = unit ? ` ${unit}` : '';
    const rows = [
      {label: translate('metrics_min'), value: this.formatDecimal(s.lowestMargin), unit},
      {label: translate('metrics_max'), value: this.formatDecimal(s.highestMargin), unit},
      ...(!['PoE Class', 'Link Speed'].includes(s.metric)
        ? [{label: translate('metrics_average_short'), value: this.formatDecimal(s.averageMargin), unit}]
        : []),
    ];
    return (
      <div key={s.metric} className={classes.marginCard}>
        <div className={classnames(classes.marginCardMetric, classes.summaryCardTitle)}>{s.metric}</div>
        {rows.map(row => (
          <div key={row.label} className={classes.marginCardRow}>
            <span className={classes.marginCardRowLabel}>{row.label}</span>
            <span className={classes.marginCardRowValue}>{row.value}</span>
            {row.unit && <span className={classes.marginCardRowUnit}>{row.unit}</span>}
          </div>
        ))}
        {s.chartData && s.chartData.length > 0 && (
          <div className={classes.chartIconWrapper}>
            <ScatterChartIcon
              size={20}
              className={classes.chartIcon}
              onClick={() => this.openScatterModal(s.metric, s.chartData, scatterUnit)}
            />
          </div>
        )}
      </div>
    );
  }

  renderMobileSummary() {
    const {metrics, classes, translate} = this.props;
    const mobileSummary = (metrics?.mobileSummary || []).filter(s => {
      if (!s || s.metric !== 'Link Speed') return !!s;

      return (s.chartData && s.chartData.length > 0)
        || [s.lowestMargin, s.highestMargin, s.averageMargin].some(value => value !== null && value !== undefined);
    });
    if (mobileSummary.length === 0) return null;

    return (
      <View marginTop="size-400">
        <Heading level={3} marginBottom="size-200">{translate('metrics_vdv_summary')}</Heading>
        <div className={classes.gridAutoFill}>
          {mobileSummary.map(s => this.renderMobileMarginCard(s))}
        </div>
      </View>
    );
  }

  renderCopperSummary() {
    const {metrics, classes, translate} = this.props;
    if (!metrics || !metrics.copperSummary || metrics.copperSummary.length === 0) return null;

    return (
      <View marginTop="size-400">
        <Heading level={3} marginBottom="size-200">{translate('metrics_copper_summary')}</Heading>
        <div className={classes.gridAutoFit}>
          {metrics.copperSummary.map(s => this.renderMarginCard(s))}
        </div>
      </View>
    );
  }

  renderCoaxSummary() {
    const {metrics, classes, translate} = this.props;
    if (!metrics || !metrics.coaxSummary || metrics.coaxSummary.length === 0) return null;

    return (
      <View marginTop="size-400">
        <Heading level={3} marginBottom="size-200">{translate('metrics_coax_summary')}</Heading>
        <div className={classes.gridAutoFill}>
          {metrics.coaxSummary.map(s => this.renderMarginCard(s))}
        </div>
      </View>
    );
  }

  renderFiberSummary() {
    const {metrics, classes, translate} = this.props;
    const fs = metrics ? metrics.fiberSummary : null;
    if (!fs) return null;

    const unit = fs.cableLengthUnit ?? '';
    const cards = [
      {label: translate('metrics_highest_insertion_loss'), value: this.formatDecimal(fs.highestInsertionLoss), unit: `dB  |  ${fs.highestInsertionLossTestName ?? ''}`, labelClassName: classes.summaryCardTitle, onChartClick: fs.insertionLossChartData?.length > 0 ? () => this.openFiberScatterModal(translate('metrics_insertion_loss'), fs.insertionLossChartData, ' dB') : null},
      {label: translate('metrics_average_insertion_loss'), value: this.formatDecimal(fs.averageInsertionLoss), unit: "dB", labelClassName: classes.summaryCardTitle, onChartClick: fs.insertionLossChartData?.length > 0 ? () => this.openFiberScatterModal(translate('metrics_insertion_loss'), fs.insertionLossChartData, ' dB') : null},
      {label: translate('metrics_lowest_margin'), value: this.formatDecimal(fs.lowestMargin), unit: `dB  |  ${fs.lowestMarginTestName ?? ''}`, labelClassName: classes.summaryCardTitle, onChartClick: fs.marginChartData?.length > 0 ? () => this.openFiberScatterModal(translate('metrics_margin'), fs.marginChartData, ' dB') : null},
      {label: translate('metrics_average_margin'), value: this.formatDecimal(fs.averageMargin), unit: "dB", labelClassName: classes.summaryCardTitle, onChartClick: fs.marginChartData?.length > 0 ? () => this.openFiberScatterModal(translate('metrics_margin'), fs.marginChartData, ' dB') : null},
      {label: translate('metrics_longest_cable'), value: fs.longestCableLength ?? translate('metrics_not_available'), unit: `${unit}  |  ${fs.longestCableTestName ?? ''}`, labelClassName: classes.summaryCardTitle, onChartClick: fs.cableLengthChartData?.length > 0 ? () => this.openFiberScatterModal(translate('metrics_cable_length'), fs.cableLengthChartData, unit ? ` ${unit}` : '') : null},
      {label: translate('metrics_average_cable_length'), value: fs.averageCableLength ?? translate('metrics_not_available'), unit, labelClassName: classes.summaryCardTitle, onChartClick: fs.cableLengthChartData?.length > 0 ? () => this.openFiberScatterModal(translate('metrics_cable_length'), fs.cableLengthChartData, unit ? ` ${unit}` : '') : null},
    ];

    return (
      <View marginTop="size-400">
        <Heading level={3} marginBottom="size-200">{translate('metrics_fiber_summary')}</Heading>
        <div className={classes.gridAutoFit}>
          {cards.map(card => this.renderMetricCard(card))}
        </div>
      </View>
    );
  }

  formatDecimal = (value, decimals = 2) => {
    if (value === null || value === undefined) return this.props.translate('metrics_not_available');
    return Number(value).toFixed(decimals);
  };

  renderMetricCard(props) {
    const {classes} = this.props;
    return (
      <div key={props.label} className={classes.metricCard}>
        <div className={classes.metricCardInner}>
          <Text UNSAFE_className={classnames(classes.metricCardLabel, props.labelClassName)}>
            {props.label}
          </Text>
          <Heading level={2} marginBottom={0} marginTop={0} UNSAFE_className={classes.metricCardValue}>
            {props.value}
          </Heading>
          <Flex alignItems="center" gap="size-100" UNSAFE_className={classes.metricCardUnitRow}>
            <Text UNSAFE_className={classes.metricCardUnit}>
              {props.unit}
            </Text>
          </Flex>
          {props.onChartClick && (
            <div className={classes.chartIconWrapper}>
              <ScatterChartIcon
                size={20}
                className={classes.chartIcon}
                onClick={props.onChartClick}
              />
            </div>
          )}
        </div>
      </div>
    )
  }

  render() {
    const {metrics, activeLanguage, impersonating, softwareUpdate, classes, translate} = this.props;
    const {chartModalOpen, chartModalTitle, chartModalData, scatterModalOpen, scatterModalTitle, scatterModalData, scatterModalUnit} = this.state;
    const extraTopOffset = impersonating && softwareUpdate ? 120 : impersonating || softwareUpdate ? 60 : 0;

    return (
      <Provider theme={defaultTheme} colorScheme="light" locale={activeLanguage}>
        <View padding="size-400" UNSAFE_style={{ marginTop: extraTopOffset }} UNSAFE_className={classes.metricsPage}>
          <Heading level={1} marginBottom="size-300" marginTop="size-700" UNSAFE_className={classes.metricsPageTitle}>{this.props.translate('metrics_organisation_metrics')}</Heading>

          {this.renderFilters()}

          {metrics &&
            <View UNSAFE_className={classes.metricsResults}>
              {this.renderHeader()}
              {this.renderCableTypeSummary()}
              {this.renderTechnicians()}
              {this.renderCopperSummary()}
              {this.renderCoaxSummary()}
              {this.renderFiberSummary()}
              {this.renderMobileSummary()}
            </View>
          }
        </View>
        {this.renderProgressDialog()}
        <MetricsBarGraphModal
          isOpen={chartModalOpen}
          onClose={this.closeChartModal}
          title={chartModalTitle}
          datasets={chartModalData}
          activeLanguage={activeLanguage}
          translate={translate}
        />
        <MetricsScatterGraphModal
          isOpen={scatterModalOpen}
          onClose={this.closeScatterModal}
          title={scatterModalTitle}
          datasets={scatterModalData}
          yAxisUnit={scatterModalUnit}
          activeLanguage={activeLanguage}
          translate={translate}
        />
      </Provider>
    );
  }
}

export default withStyles(styles)(Metrics);
